import type { ModificationsMap, Modifier } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Filters the given list of additions, returning only those that qualify
 * as free additions based on the provided conditions.
 */
export function filterFreeAdditions(
  additions: Modifier[],
  allModificationsMap: ModificationsMap,
  freeAdditionGroups: FreeAdditionGroups,
): Modifier[] {
  const filterFreeAddition = filterFreeAdditionFactory(allModificationsMap);
  const initialState = { filteredAdditions: [], freeAdditionGroups };

  const { filteredAdditions } = additions.reduce<FilteredAdditionsState>(
    filterFreeAddition,
    initialState,
  );

  return filteredAdditions;
}

// ─────────────────────────────────────────────────────────────────────────────

function filterFreeAdditionFactory(allModificationsMap: ModificationsMap) {
  return function (state: FilteredAdditionsState, addition: Modifier) {
    const { filteredAdditions, freeAdditionGroups } = state;

    const { ingredientId } = addition;
    const modification = allModificationsMap.get(ingredientId);

    if (!modification) {
      return state;
    }

    const { modificationGroupName } = modification;

    const freeAdditionsQuantity = freeAdditionGroups[modificationGroupName] ?? 0; // prettier-ignore
    const isFreeAddition = freeAdditionsQuantity > 0;

    // ─── Free Addition ───────────────────────────────────────────────────

    if (isFreeAddition) {
      return {
        ...state,
        freeAdditionGroups: {
          ...freeAdditionGroups,
          [modificationGroupName]: freeAdditionsQuantity - 1,
        },
      };
    }

    // ─── Paid Addition ───────────────────────────────────────────────────

    return {
      ...state,
      filteredAdditions: [...filteredAdditions, addition],
    };
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type FreeAdditionGroups = Record<string, number>;

type FilteredAdditionsState = {
  filteredAdditions: Modifier[];
  freeAdditionGroups: FreeAdditionGroups;
};
